import {
	Building04,
	ChevronRight,
	Hash01,
	MarkerPin03,
	Ticket01,
	User01,
} from "@untitledui/icons";
import type { FC, ReactElement, ReactNode } from "react";

import { BadgeWithIcon } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Label } from "@/components/base/label/label";
import { EmptyValue } from "@/components/common/EmptyValue";
import {
	Alert,
	AlertSemantic,
	AlertType,
} from "@/components/Molecules/Alert/Alert";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";

import { formatLatitude, formatLongitude } from "./format-map-coordinate";
import { MarkerPopupAttributeRow } from "./MarkerPopupAttributeRow";
import {
	MarkerPopupEntity,
	type MarkerPopupEntity as MarkerPopupEntityValue,
} from "./marker-popup-entity";
import {
	MarkerPopupBodyState,
	type MarkerPopupPointDetails,
} from "./marker-popup-point-details";

type IconComponent = FC<{ className?: string }>;

export interface MarkerPopupBodyProps {
	/** Figma Entity variant. @default "job" */
	entity?: MarkerPopupEntityValue;
	/** Figma MarkerPopup/Body State. @default "main-no-link" */
	state?: MarkerPopupBodyState;
	title?: string;
	subtitle?: string;
	idLabel?: string;
	badgeLabel?: string;
	typeLabel?: string;
	assignee?: string;
	dueDate?: string;
	actionLabel?: string;
	badgeIcon?: IconComponent;
	onAction?: () => void;
	onPointDetails?: () => void;
	nonVertexMessage?: string;
	pointDetails?: MarkerPopupPointDetails;
	className?: string;
}

const ENTITY_BADGE_ICON: Record<MarkerPopupEntityValue, IconComponent> = {
	[MarkerPopupEntity.Job]: Building04,
	[MarkerPopupEntity.Asset]: Building04,
	[MarkerPopupEntity.Record]: Building04,
	[MarkerPopupEntity.Ticket]: Ticket01,
};

/** Matches Figma Scroll=yes fade so the last row can scroll above the gradient. */
const POINT_SCROLL_FADE_PX = 45;

function toDisplayValue(value: string | undefined): ReactNode {
	return value != null && value !== "" ? value : <EmptyValue />;
}

function PointScrollBody({ children }: { children: ReactNode }): ReactElement {
	return (
		<div
			className="relative h-full min-h-0 w-full overflow-hidden"
			data-testid="marker-popup-point-scroll"
		>
			<div className="h-full overflow-x-clip overflow-y-auto overscroll-contain">
				<div className="flex flex-col gap-2">
					{children}
					<div
						aria-hidden
						className="shrink-0"
						style={{ height: POINT_SCROLL_FADE_PX }}
					/>
				</div>
			</div>
			<div
				aria-hidden
				className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary to-transparent"
				style={{ height: POINT_SCROLL_FADE_PX }}
			/>
		</div>
	);
}

type PopupLabelFn = (key: string) => string;

function CoordinatePair({
	latitude,
	longitude,
	t,
}: {
	latitude?: number;
	longitude?: number;
	t: PopupLabelFn;
}): ReactElement {
	return (
		<div className="flex items-start gap-6">
			<MarkerPopupAttributeRow
				label={t("main.map.setLocation.latitude")}
				value={latitude != null ? formatLatitude(latitude) : <EmptyValue />}
			/>
			<MarkerPopupAttributeRow
				label={t("main.map.setLocation.longitude")}
				value={longitude != null ? formatLongitude(longitude) : <EmptyValue />}
			/>
		</div>
	);
}

/** Vertex index row — only for line/polygon vertices (`pointDetails.vertex`). */
function VertexAttributeRow({
	vertex,
	t,
}: {
	vertex: string | undefined;
	t: PopupLabelFn;
}): ReactElement | null {
	if (vertex == null || vertex === "") return null;
	return (
		<MarkerPopupAttributeRow
			label={t("main.map.popup.vertex")}
			value={toDisplayValue(vertex)}
		/>
	);
}

function DeviceGpsFields({
	pointDetails,
	t,
}: {
	pointDetails: MarkerPopupPointDetails;
	t: PopupLabelFn;
}): ReactElement {
	return (
		<>
			<VertexAttributeRow vertex={pointDetails.vertex} t={t} />
			<MarkerPopupAttributeRow
				label={t("main.map.popup.source")}
				value={toDisplayValue(pointDetails.source)}
			/>
			<CoordinatePair
				latitude={pointDetails.latitude}
				longitude={pointDetails.longitude}
				t={t}
			/>
			<MarkerPopupAttributeRow
				label={t("main.map.popup.elevation")}
				value={toDisplayValue(pointDetails.elevation)}
			/>
			<MarkerPopupAttributeRow
				label={t("main.map.popup.timestamp")}
				value={toDisplayValue(pointDetails.timestamp)}
			/>
			<MarkerPopupAttributeRow
				label={t("main.map.popup.accuracy")}
				value={toDisplayValue(pointDetails.accuracy)}
			/>
		</>
	);
}

function BluetoothRtkFields({
	pointDetails,
	t,
}: {
	pointDetails: MarkerPopupPointDetails;
	t: PopupLabelFn;
}): ReactElement {
	return (
		<>
			<VertexAttributeRow vertex={pointDetails.vertex} t={t} />
			<MarkerPopupAttributeRow
				label={t("main.map.popup.source")}
				value={toDisplayValue(pointDetails.source)}
			/>
			<CoordinatePair
				latitude={pointDetails.latitude}
				longitude={pointDetails.longitude}
				t={t}
			/>
			<MarkerPopupAttributeRow
				label={t("main.map.popup.elevation")}
				value={toDisplayValue(pointDetails.elevation)}
			/>
			<MarkerPopupAttributeRow
				label={t("main.map.popup.timestamp")}
				value={toDisplayValue(pointDetails.timestamp)}
			/>
			<MarkerPopupAttributeRow
				label={t("main.map.popup.horizontalAccuracy")}
				value={toDisplayValue(pointDetails.horizontalAccuracy)}
			/>
			<MarkerPopupAttributeRow
				label={t("main.map.popup.verticalAccuracy")}
				value={toDisplayValue(pointDetails.verticalAccuracy)}
			/>
			<MarkerPopupAttributeRow
				label={t("main.map.popup.fixType")}
				value={toDisplayValue(pointDetails.fixType)}
			/>
			<MarkerPopupAttributeRow
				label={t("main.map.popup.satelliteCount")}
				value={toDisplayValue(pointDetails.satelliteCount)}
			/>
			<MarkerPopupAttributeRow
				label={t("main.map.popup.hdop")}
				value={toDisplayValue(pointDetails.hdop)}
			/>
			<MarkerPopupAttributeRow
				label={t("main.map.popup.depth")}
				value={toDisplayValue(pointDetails.depth)}
			/>
			<MarkerPopupAttributeRow
				label={t("main.map.popup.current")}
				value={toDisplayValue(pointDetails.current)}
			/>
			<MarkerPopupAttributeRow
				label={t("main.map.popup.frequency")}
				value={toDisplayValue(pointDetails.frequency)}
			/>
			<MarkerPopupAttributeRow
				label={t("main.map.popup.gain")}
				value={toDisplayValue(pointDetails.gain)}
			/>
		</>
	);
}

/**
 * Body slot for Map/MarkerPopup — Figma MarkerPopup/Body (73:3934).
 *
 * Main states render entity summary + optional Point details link and View CTA.
 * Point states render AttributeRow lists (Coordinate Entry / Device GPS / Bluetooth RTK).
 */
export function MarkerPopupBody({
	entity = MarkerPopupEntity.Job,
	state = MarkerPopupBodyState.MainNoLink,
	title,
	subtitle,
	idLabel,
	badgeLabel,
	typeLabel,
	assignee,
	dueDate,
	actionLabel,
	badgeIcon,
	onAction,
	onPointDetails,
	nonVertexMessage,
	pointDetails,
	className,
}: MarkerPopupBodyProps): ReactElement {
	const { t } = useAppTranslation();
	const details = pointDetails ?? {};
	const isPoint =
		state === MarkerPopupBodyState.PointCoordinateEntry ||
		state === MarkerPopupBodyState.PointDeviceGps ||
		state === MarkerPopupBodyState.PointBluetoothRtk;

	if (state === MarkerPopupBodyState.PointCoordinateEntry) {
		return (
			<div className={cx("flex w-full flex-col gap-2", className)}>
				<VertexAttributeRow vertex={details.vertex} t={t} />
				<MarkerPopupAttributeRow
					label={t("main.map.popup.source")}
					value={toDisplayValue(details.source)}
				/>
				<CoordinatePair
					latitude={details.latitude}
					longitude={details.longitude}
					t={t}
				/>
			</div>
		);
	}

	if (state === MarkerPopupBodyState.PointDeviceGps) {
		return (
			<div className={cx("flex h-full min-h-0 w-full flex-col", className)}>
				<PointScrollBody>
					<DeviceGpsFields pointDetails={details} t={t} />
				</PointScrollBody>
			</div>
		);
	}

	if (state === MarkerPopupBodyState.PointBluetoothRtk) {
		return (
			<div className={cx("flex h-full min-h-0 w-full flex-col", className)}>
				<PointScrollBody>
					<BluetoothRtkFields pointDetails={details} t={t} />
				</PointScrollBody>
			</div>
		);
	}

	const showMeta = Boolean(assignee || dueDate);
	const resolvedBadgeIcon = badgeIcon ?? ENTITY_BADGE_ICON[entity];
	const showPointDetailsLink =
		state === MarkerPopupBodyState.MainLink &&
		onPointDetails != null &&
		nonVertexMessage == null;

	return (
		<div
			className={cx("flex w-full flex-col gap-3", className)}
			data-state={state}
			data-entity={entity}
			data-point={isPoint ? "true" : "false"}
		>
			<div className="flex w-full min-w-0 flex-col">
				{title ? (
					<p className="truncate font-display text-sm font-extrabold leading-5 text-primary">
						{title}
					</p>
				) : null}
				{subtitle ? (
					<p className="truncate font-display text-xs font-extrabold leading-[18px] tracking-[0.24px] text-quaternary">
						{subtitle}
					</p>
				) : null}
				{idLabel ? (
					<Label size="sm" type="light" icon={Hash01} className="mt-0.5">
						{idLabel}
					</Label>
				) : null}
				{showMeta ? (
					<div className="mt-1 flex min-w-0 items-center gap-1.5 text-sm font-medium text-quaternary">
						<User01 className="size-3.5 shrink-0" aria-hidden />
						{assignee ? <span className="truncate">{assignee}</span> : null}
						{assignee && dueDate ? <span aria-hidden>·</span> : null}
						{dueDate ? (
							<span className="shrink-0 truncate">{dueDate}</span>
						) : null}
					</div>
				) : null}
			</div>

			{(badgeLabel || typeLabel) && (
				<div
					data-testid="marker-popup-badge-type"
					className="flex w-full flex-wrap items-center gap-x-4 gap-y-1.5 overflow-hidden"
				>
					{badgeLabel ? (
						<BadgeWithIcon
							color="blue"
							size="md"
							type="pill-color"
							iconLeading={resolvedBadgeIcon}
							className="max-w-full shrink-0 bg-utility-blue-600! text-fg-white! ring-utility-blue-600! [&_svg]:text-fg-white!"
						>
							{badgeLabel}
						</BadgeWithIcon>
					) : null}
					{typeLabel ? (
						<Label
							size="md"
							type="light"
							icon={MarkerPin03}
							className="max-w-full shrink-0"
						>
							{typeLabel}
						</Label>
					) : null}
				</div>
			)}

			{nonVertexMessage ? (
				<Alert
					type={AlertType.Alert}
					semantic={AlertSemantic.Info}
					title={nonVertexMessage}
					showDescription={false}
					className="py-3"
				/>
			) : null}

			{showPointDetailsLink ? (
				<Button
					color="link-color"
					size="sm"
					iconTrailing={ChevronRight}
					onClick={onPointDetails}
				>
					{t("main.map.popup.pointDetails")}
				</Button>
			) : null}

			{onAction != null ? (
				<Button
					color="secondary"
					size="sm"
					className="w-full"
					onClick={onAction}
				>
					{actionLabel}
				</Button>
			) : null}
		</div>
	);
}
